import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar as CalIcon, Apple, Globe, Copy, Check, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ICS_URL = `${API_BASE}/api/calendar.ics`;
// webcal:// protocol triggers Apple Calendar / Outlook to subscribe directly
const WEBCAL_URL = ICS_URL.replace(/^https?:/, "webcal:");
const GCAL_URL = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(ICS_URL)}`;

function fmtDate(iso) {
  if (!iso) return "TBD";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function PlatformCalendar() {
  const [events, setEvents] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/events?upcoming_only=false").catch(() => ({ data: [] })),
      api.get("/platform/meetings/mine").catch(() => ({ data: { meetings: [] } })),
    ]).then(([evRes, mtgRes]) => {
      const evs = (evRes.data || []).map((e) => ({ ...e, _kind: "event" }));
      const meetings = (mtgRes.data?.meetings || [])
        .filter((m) => m.start_at) // only scheduled ones land on the calendar
        .map((m) => ({
          _kind: "meeting",
          event_id: m.meeting_id,
          title: `ABS · ${m.title}`,
          event_date: m.start_at,
          location: m.location || "Online · Jitsi",
          mode: m.mode || "online",
          description: m.description,
          code: m.code,
          duration_min: m.duration_min,
        }));
      const merged = [...evs, ...meetings].sort((a, b) => {
        const ad = new Date(a.event_date || a.date || 0).getTime();
        const bd = new Date(b.event_date || b.date || 0).getTime();
        return ad - bd;
      });
      setEvents(merged);
    });
  }, []);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return events.filter((e) => new Date(e.event_date || e.date || 0).getTime() >= now - 86_400_000);
  }, [events]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ICS_URL);
      setCopied(true);
      toast.success("Calendar URL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="platform-calendar">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Calendar · Auto-sync</p>
      <h1 className="font-display text-4xl text-navy">Sangha Calendar</h1>
      <p className="mt-2 text-navy/65 max-w-2xl">
        Subscribe once, every ABGN-UK event lands in your phone's calendar automatically. Updates flow
        through within an hour. Works with Google, Apple, Outlook and any iCal-compatible app.
      </p>

      {/* Subscribe row */}
      <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="calendar-subscribe-buttons">
        <a
          href={GCAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="calendar-google-btn"
          className="flex items-center gap-3 bg-white border border-navy/10 hover:border-saffron rounded-lg p-4 transition-colors group"
        >
          <div className="w-10 h-10 bg-blue-500/10 rounded-md flex items-center justify-center">
            <Globe size={20} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-navy">Google Calendar</div>
            <div className="text-xs text-navy/55">One-click add to your account</div>
          </div>
          <ExternalLink size={14} className="text-navy/40 group-hover:text-saffron" />
        </a>
        <a
          href={WEBCAL_URL}
          data-testid="calendar-apple-btn"
          className="flex items-center gap-3 bg-white border border-navy/10 hover:border-saffron rounded-lg p-4 transition-colors group"
        >
          <div className="w-10 h-10 bg-navy/5 rounded-md flex items-center justify-center">
            <Apple size={20} className="text-navy" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-navy">Apple Calendar / Outlook</div>
            <div className="text-xs text-navy/55">Opens "Subscribe" in your default app</div>
          </div>
          <ExternalLink size={14} className="text-navy/40 group-hover:text-saffron" />
        </a>
        <button
          onClick={copy}
          data-testid="calendar-copy-url"
          className="flex items-center gap-3 bg-white border border-navy/10 hover:border-saffron rounded-lg p-4 transition-colors group text-left"
        >
          <div className="w-10 h-10 bg-saffron/10 rounded-md flex items-center justify-center">
            {copied ? <Check size={20} className="text-saffron" /> : <Copy size={20} className="text-saffron" />}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-navy">Any other calendar</div>
            <div className="text-xs text-navy/55">{copied ? "Copied!" : "Copy the iCal URL"}</div>
          </div>
        </button>
      </div>
      <p className="mt-3 text-xs text-navy/45 italic">
        💡 Tip: subscribing (rather than importing) means new events automatically appear later, you
        only have to do it once.
      </p>

      {/* Upcoming list */}
      <section className="mt-10" data-testid="calendar-event-list">
        <h2 className="font-display text-2xl text-navy mb-4">{upcoming.length} upcoming event{upcoming.length === 1 ? "" : "s"}</h2>
        {upcoming.length === 0 ? (
          <div className="bg-white border border-navy/10 rounded-xl p-12 text-center text-navy/60">
            <CalIcon size={28} className="mx-auto mb-3 text-saffron" />
            No upcoming events scheduled. Check back soon.
          </div>
        ) : (
          <div className="bg-white border border-navy/10 rounded-xl divide-y divide-navy/5 overflow-hidden">
            {upcoming.map((e) => {
              const dt = new Date(e.event_date || e.date || 0);
              return (
                <div key={e.event_id || e.id} className="flex items-center gap-4 p-4" data-testid={`calendar-event-${e.event_id || e.id}`}>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-saffron/20 to-saffron/5 flex flex-col items-center justify-center flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-saffron">
                      {dt.toLocaleDateString("en-GB", { month: "short" })}
                    </div>
                    <div className="text-2xl font-display text-navy leading-none">
                      {dt.getDate()}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-navy/45 mt-0.5">
                      {dt.toLocaleDateString("en-GB", { weekday: "short" })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-navy">{e.title}</div>
                    <div className="text-xs text-navy/60 mt-0.5">
                      {fmtDate(e.event_date || e.date)} · {e.location || "Location TBC"} · {e.mode || e.format || "in-person"}
                    </div>
                    {e.description && (
                      <div className="text-xs text-navy/65 mt-1 line-clamp-2">{e.description}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
